import { mountAdminMarathonItemCreator } from "./admin-shop-creator";
import {
  createAdminMarathonSegment,
  deleteAdminMarathonSegment,
  fetchAdminMarathonCosmetics,
  fetchAdminMarathonCrates,
  fetchAdminMarathonSegments,
  fetchAdminMarathonShopItems,
  reorderAdminMarathonSegments,
  updateAdminMarathonSegment,
  type AdminMarathonSegment,
  type MarathonRewardKind,
} from "./marathon/api";

const MODE_OPTIONS: Array<{ id: string; label: string }> = [
  { id: "normal", label: "Normal" },
  { id: "wasd", label: "WASD" },
  { id: "superdot", label: "Superdot" },
  { id: "roulette", label: "Roulette" },
  { id: "highway", label: "Highway" },
  { id: "darkness", label: "Darkness" },
  { id: "mirror", label: "Mirror" },
  { id: "snake", label: "Snake" },
  { id: "combo", label: "Combo" },
  { id: "colorrush", label: "Color Rush" },
  { id: "heat", label: "Heat" },
  { id: "hunter", label: "Hunter" },
  { id: "pop", label: "POP" },
  { id: "counter", label: "Counter" },
  { id: "aim", label: "AIM" },
  { id: "hook", label: "Hook" },
  { id: "tilt", label: "Tilt" },
  { id: "orbit", label: "Orbit" },
  { id: "delay", label: "Delay" },
  { id: "progression", label: "Progression" },
  { id: "speedrun", label: "SpeedRun" },
  { id: "penalty", label: "Golf" },
];

function needsLevel(modeId: string) {
  return modeId === "speedrun" || modeId === "penalty";
}

function isRace(modeId: string) {
  return modeId === "speedrun" || modeId === "penalty";
}

export type AdminMarathonApi = { refresh: () => Promise<void> };

export function mountAdminMarathon(root: HTMLElement): AdminMarathonApi {
  root.innerHTML = "";
  root.classList.add("adminMarathonRoot");

  const title = document.createElement("h2");
  title.className = "adminSectionTitle";
  title.textContent = "Marathon";

  const hint = document.createElement("p");
  hint.className = "accessHint";
  hint.textContent =
    "Build the Compete → Marathon sequence. Players chain legs without pauses; dying ends the whole run. Segment rewards grant once on first reach.";

  const status = document.createElement("p");
  status.className = "accessHint";

  const list = document.createElement("div");
  list.className = "adminMarathonList";

  const form = document.createElement("div");
  form.className = "adminMarathonForm modeBox";

  const formTitle = document.createElement("h3");
  formTitle.textContent = "Add / edit leg";

  const modeSelect = document.createElement("select");
  modeSelect.className = "formInput";
  for (const m of MODE_OPTIONS) {
    const o = document.createElement("option");
    o.value = m.id;
    o.textContent = m.label;
    modeSelect.appendChild(o);
  }

  const metricSelect = document.createElement("select");
  metricSelect.className = "formInput";
  for (const [v, label] of [
    ["score", "Score"],
    ["time", "Time (seconds)"],
  ] as const) {
    const o = document.createElement("option");
    o.value = v;
    o.textContent = label;
    metricSelect.appendChild(o);
  }

  const goalInput = document.createElement("input");
  goalInput.className = "formInput";
  goalInput.type = "number";
  goalInput.min = "1";
  goalInput.value = "100";

  const levelInput = document.createElement("input");
  levelInput.className = "formInput";
  levelInput.type = "number";
  levelInput.min = "1";
  levelInput.value = "1";

  const rewardKindSelect = document.createElement("select");
  rewardKindSelect.className = "formInput";
  for (const [v, label] of [
    ["none", "No reward"],
    ["dots", "Dots"],
    ["exclusive_item", "Exclusive item"],
    ["shop_item", "Shop item"],
    ["free_crate", "Free crate roll"],
  ] as const) {
    const o = document.createElement("option");
    o.value = v;
    o.textContent = label;
    rewardKindSelect.appendChild(o);
  }

  const dotsInput = document.createElement("input");
  dotsInput.className = "formInput";
  dotsInput.type = "number";
  dotsInput.min = "1";
  dotsInput.value = "500";

  const exclusiveSelect = document.createElement("select");
  exclusiveSelect.className = "formInput";

  const shopSelect = document.createElement("select");
  shopSelect.className = "formInput";

  const crateSelect = document.createElement("select");
  crateSelect.className = "formInput";

  const creatorMount = document.createElement("div");
  creatorMount.className = "adminMarathonCreatorMount";

  let editingId: string | null = null;
  let segments: AdminMarathonSegment[] = [];
  let dragFrom: number | null = null;

  const row = (label: string, el: HTMLElement) => {
    const r = document.createElement("div");
    r.className = "formRow";
    r.append(
      Object.assign(document.createElement("span"), { className: "formLabel", textContent: label }),
      el,
    );
    return r;
  };

  const levelRow = row("Level #", levelInput);
  const dotsRow = row("Dots amount", dotsInput);
  const exclusiveRow = row("Exclusive item", exclusiveSelect);
  const shopRow = row("Shop item", shopSelect);
  const crateRow = row("Crate", crateSelect);

  function syncModeUi() {
    const modeId = modeSelect.value;
    const race = isRace(modeId);
    levelRow.classList.toggle("hidden", !needsLevel(modeId));
    if (race) {
      metricSelect.value = "time";
      metricSelect.disabled = true;
    } else {
      metricSelect.disabled = false;
    }
  }

  function syncRewardUi() {
    const kind = rewardKindSelect.value as MarathonRewardKind;
    dotsRow.classList.toggle("hidden", kind !== "dots");
    exclusiveRow.classList.toggle("hidden", kind !== "exclusive_item");
    shopRow.classList.toggle("hidden", kind !== "shop_item");
    crateRow.classList.toggle("hidden", kind !== "free_crate");
    creatorMount.classList.toggle("hidden", kind !== "exclusive_item");
  }

  modeSelect.onchange = syncModeUi;
  rewardKindSelect.onchange = syncRewardUi;

  async function fillPickers() {
    const [exclusives, shopItems, crates] = await Promise.all([
      fetchAdminMarathonCosmetics().catch(() => []),
      fetchAdminMarathonShopItems().catch(() => []),
      fetchAdminMarathonCrates().catch(() => []),
    ]);
    exclusiveSelect.innerHTML = "";
    const blankEx = document.createElement("option");
    blankEx.value = "";
    blankEx.textContent = "— Select —";
    exclusiveSelect.appendChild(blankEx);
    for (const item of exclusives) {
      const o = document.createElement("option");
      o.value = item.id;
      o.textContent = `${item.name}${item.marathonOnly ? "" : " (shared)"}`;
      exclusiveSelect.appendChild(o);
    }
    shopSelect.innerHTML = "";
    const blankShop = document.createElement("option");
    blankShop.value = "";
    blankShop.textContent = "— Select —";
    shopSelect.appendChild(blankShop);
    for (const item of shopItems) {
      const o = document.createElement("option");
      o.value = item.id;
      o.textContent = item.name;
      shopSelect.appendChild(o);
    }
    crateSelect.innerHTML = "";
    const blankCrate = document.createElement("option");
    blankCrate.value = "";
    blankCrate.textContent = "— Select —";
    crateSelect.appendChild(blankCrate);
    for (const c of crates) {
      const o = document.createElement("option");
      o.value = c.id;
      o.textContent = c.name;
      crateSelect.appendChild(o);
    }
  }

  function resetForm() {
    editingId = null;
    formTitle.textContent = "Add / edit leg";
    modeSelect.value = "normal";
    metricSelect.value = "score";
    goalInput.value = "100";
    levelInput.value = "1";
    rewardKindSelect.value = "none";
    dotsInput.value = "500";
    exclusiveSelect.value = "";
    shopSelect.value = "";
    crateSelect.value = "";
    syncModeUi();
    syncRewardUi();
  }

  function loadIntoForm(seg: AdminMarathonSegment) {
    editingId = seg.id;
    formTitle.textContent = `Edit leg — ${seg.modeLabel}`;
    modeSelect.value = seg.modeId;
    metricSelect.value = seg.metric;
    goalInput.value = String(seg.goalValue);
    levelInput.value = String(seg.levelIndex || 1);
    rewardKindSelect.value = seg.rewardKind || "none";
    dotsInput.value = String(seg.rewardDots || 500);
    exclusiveSelect.value = seg.rewardCosmeticId || "";
    shopSelect.value = seg.rewardCosmeticId || "";
    crateSelect.value = seg.rewardCrateId || "";
    syncModeUi();
    syncRewardUi();
  }

  function renderList() {
    list.innerHTML = "";
    if (!segments.length) {
      const empty = document.createElement("p");
      empty.className = "accessHint";
      empty.textContent = "No legs yet — add the first segment below.";
      list.appendChild(empty);
      return;
    }
    segments.forEach((seg, index) => {
      const card = document.createElement("div");
      card.className = "adminMarathonCard";
      card.draggable = true;
      card.dataset.index = String(index);
      card.innerHTML = `<div class="adminMarathonCardMain">
        <span class="adminMarathonCardOrder">#${index + 1}</span>
        <strong>${seg.modeLabel}</strong>
        <span class="accessHint">${seg.goalLabel}</span>
        <span class="accessHint">${seg.rewardKind && seg.rewardKind !== "none" ? seg.rewardKind : "no reward"}</span>
      </div>`;
      const actions = document.createElement("div");
      actions.className = "adminMarathonCardActions";
      const editBtn = document.createElement("button");
      editBtn.type = "button";
      editBtn.className = "btn";
      editBtn.textContent = "Edit";
      editBtn.onclick = () => loadIntoForm(seg);
      const delBtn = document.createElement("button");
      delBtn.type = "button";
      delBtn.className = "btn";
      delBtn.textContent = "Delete";
      delBtn.onclick = async () => {
        if (!confirm(`Delete leg #${index + 1} (${seg.modeLabel})?`)) return;
        try {
          await deleteAdminMarathonSegment(seg.id);
          await refresh();
        } catch (e) {
          status.textContent = e instanceof Error ? e.message : "Delete failed.";
        }
      };
      actions.append(editBtn, delBtn);
      card.appendChild(actions);

      card.addEventListener("dragstart", () => {
        dragFrom = index;
        card.classList.add("adminMarathonCard--dragging");
      });
      card.addEventListener("dragend", () => {
        dragFrom = null;
        card.classList.remove("adminMarathonCard--dragging");
      });
      card.addEventListener("dragover", (e) => {
        e.preventDefault();
      });
      card.addEventListener("drop", async (e) => {
        e.preventDefault();
        if (dragFrom == null || dragFrom === index) return;
        const order = segments.map((s) => s.id);
        const [moved] = order.splice(dragFrom, 1);
        order.splice(index, 0, moved);
        try {
          segments = await reorderAdminMarathonSegments(order);
          renderList();
          status.textContent = "Sequence reordered.";
        } catch (err) {
          status.textContent = err instanceof Error ? err.message : "Reorder failed.";
        }
      });

      list.appendChild(card);
    });
  }

  const saveBtn = document.createElement("button");
  saveBtn.type = "button";
  saveBtn.className = "btn green";
  saveBtn.textContent = "Save leg";
  saveBtn.onclick = async () => {
    const kind = rewardKindSelect.value as MarathonRewardKind;
    const body: Record<string, unknown> = {
      modeId: modeSelect.value,
      metric: metricSelect.value,
      goalValue: Number(goalInput.value) || 1,
      levelIndex: needsLevel(modeSelect.value) ? Number(levelInput.value) || 1 : null,
      rewardKind: kind,
      rewardDots: kind === "dots" ? Number(dotsInput.value) || 0 : 0,
      rewardCosmeticId:
        kind === "exclusive_item"
          ? exclusiveSelect.value || null
          : kind === "shop_item"
            ? shopSelect.value || null
            : null,
      rewardCrateId: kind === "free_crate" ? crateSelect.value || null : null,
    };
    try {
      status.textContent = "Saving…";
      if (editingId) await updateAdminMarathonSegment(editingId, body);
      else await createAdminMarathonSegment(body);
      status.textContent = "Saved.";
      resetForm();
      await refresh();
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : "Save failed.";
    }
  };

  const clearBtn = document.createElement("button");
  clearBtn.type = "button";
  clearBtn.className = "btn";
  clearBtn.textContent = "Clear form";
  clearBtn.onclick = () => {
    resetForm();
    status.textContent = "";
  };

  const actions = document.createElement("div");
  actions.className = "formRow";
  actions.append(saveBtn, clearBtn);

  form.append(
    formTitle,
    row("Mode", modeSelect),
    row("Goal type", metricSelect),
    row("Goal value", goalInput),
    levelRow,
    row("Reward", rewardKindSelect),
    dotsRow,
    exclusiveRow,
    shopRow,
    crateRow,
    creatorMount,
    actions,
  );

  root.append(title, hint, status, list, form);

  mountAdminMarathonItemCreator(creatorMount, {
    onMarathonItemPublished: (cosmetic) => {
      void fillPickers().then(() => {
        exclusiveSelect.value = cosmetic.id;
        rewardKindSelect.value = "exclusive_item";
        syncRewardUi();
        status.textContent = `Exclusive “${cosmetic.name}” ready — save the leg to attach it.`;
      });
    },
  });

  async function refresh() {
    try {
      segments = await fetchAdminMarathonSegments();
      await fillPickers();
      renderList();
      syncModeUi();
      syncRewardUi();
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : "Could not load Marathon.";
    }
  }

  resetForm();
  void refresh();

  return { refresh };
}
